import { fileRepository } from '@/api';
import { DocumentsFormStepConfiguration } from '@/api/interfaces/configuration/form/steps/documentsFormStepConfiguration';
import { FileUploadConfiguration } from '@/api/interfaces/configuration/form/base/fileUploadConfiguration';
import { UploadedFileInfo } from '@/api/interfaces/file/uploadedFileInfo';
import { appState } from '@/services/form/appState';
import { FormElement } from '@/types/forms/formElement';
import { FormList } from '@/types/forms/fields/formList';
import { UploadField } from '@/types/forms/fields/uploadField';
import { DocumentsFormStep } from '@/types/forms/formSteps/documentsFormStep';
import { ApplicationForm } from '@/types/forms/specific/applicationForm';
import { FormField } from '@/types/forms/fields/formField';

interface ReferenceModuleList {
    items: Array<{ id?: FormField<string> }>;
    createItem(id?: string): void;
    sudoRemoveAt(index: number): void;
}

const createDebugPdf = (): File => {
    const lineEnding = '\r\n';
    const content = 'BT /F1 12 Tf 20 50 Td (DEA Debug) Tj ET';
    const objects = [
        '1 0 obj\r\n<< /Type /Catalog /Pages 2 0 R >>\r\nendobj',
        '2 0 obj\r\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\r\nendobj',
        '3 0 obj\r\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 200 100] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>\r\nendobj',
        '4 0 obj\r\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\r\nendobj',
        `5 0 obj\r\n<< /Length ${content.length} >>\r\nstream\r\n${content}\r\nendstream\r\nendobj`,
    ];
    let pdf = `%PDF-1.4${lineEnding}`;
    const offsets = [0];

    objects.forEach((object) => {
        offsets.push(pdf.length);
        pdf += `${object}${lineEnding}`;
    });

    const xrefOffset = pdf.length;
    pdf += `xref${lineEnding}0 ${objects.length + 1}${lineEnding}`;
    pdf += `0000000000 65535 f ${lineEnding}`;
    offsets.slice(1).forEach((offset) => {
        pdf += `${`0000000000${offset}`.slice(-10)} 00000 n ${lineEnding}`;
    });
    pdf += `trailer${lineEnding}<< /Size ${
        objects.length + 1
    } /Root 1 0 R >>${lineEnding}startxref${lineEnding}${xrefOffset}${lineEnding}%%EOF${lineEnding}`;

    return new File([pdf], 'debug-document.pdf', {
        type: 'application/pdf',
    });
};

const setDebugFileInfo = (
    element: FormElement,
    fileInfo: UploadedFileInfo,
    configuration: DocumentsFormStepConfiguration,
    fieldIdentifier?: keyof DocumentsFormStepConfiguration['fields'],
): void => {
    if (element instanceof UploadField) {
        const fieldConfiguration = fieldIdentifier
            ? configuration.fields[fieldIdentifier]
            : undefined;
        const fileUploadConfiguration = fieldConfiguration?.configurations.find(
            (entry) => entry.type === 'fileUploadConfiguration',
        ) as FileUploadConfiguration | undefined;

        element.value = {
            ...fileInfo,
            scId: fileUploadConfiguration?.scId ?? fileInfo.scId,
        };
        return;
    }

    if (element instanceof FormList) {
        element.items.forEach((item) =>
            setDebugFileInfo(item, fileInfo, configuration),
        );
        return;
    }

    Object.entries(element).forEach(([identifier, value]) => {
        if (value instanceof FormElement) {
            setDebugFileInfo(
                value,
                fileInfo,
                configuration,
                identifier as keyof DocumentsFormStepConfiguration['fields'],
            );
        }
    });
};

const syncDocumentModuleLists = (
    documents: DocumentsFormStep,
    application: ApplicationForm,
): void => {
    const moduleLists: Array<[ReferenceModuleList, ReferenceModuleList]> = [
        [
            application.plantPerformance.generator.generatorModules,
            documents.generatorModules,
        ],
        [
            application.plantPerformance.inverter.inverterModules,
            documents.inverterModules,
        ],
        [
            application.plantPerformance.inverterStorage.inverterModules,
            documents.inverterStorageModules,
        ],
        [
            application.plantPerformance.storage.storageModules,
            documents.storageModules,
        ],
    ];

    moduleLists.forEach(([source, destination]) => {
        while (destination.items.length > source.items.length) {
            destination.sudoRemoveAt(destination.items.length - 1);
        }
        source.items.forEach((sourceItem, index) => {
            const sourceId = sourceItem.id?.value ?? undefined;
            if (!destination.items[index]) {
                destination.createItem(sourceId);
            } else if (destination.items[index].id) {
                const destinationId = destination.items[index].id;
                if (destinationId) {
                    destinationId.value = sourceId ?? null;
                }
            }
        });
    });
};

export async function fillDebugUploads(
    model: DocumentsFormStep,
    configuration: DocumentsFormStepConfiguration,
    applicationId: string,
    application: ApplicationForm,
): Promise<void> {
    const fileUploadConfiguration = configuration.fields.sitePlanWithLotNumber.configurations.find(
        (configurationEntry) =>
            configurationEntry.type === 'fileUploadConfiguration',
    ) as FileUploadConfiguration | undefined;

    if (!fileUploadConfiguration) {
        return;
    }

    appState.pendingFileUploads++;
    try {
        syncDocumentModuleLists(model, application);
        const response = await fileRepository.uploadFile(
            createDebugPdf(),
            fileUploadConfiguration.scId,
            'debug-document.pdf',
            applicationId,
        );

        if (response.fileInfo) {
            setDebugFileInfo(model, response.fileInfo, configuration);
        }
    } finally {
        appState.pendingFileUploads--;
    }
}
